import { useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";
import { CalendarIcon, CheckCircle, MapPin, Phone, Mail, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const bookingSchema = z.object({
  parent_name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email"),
  phone: z.string().min(10, "Please enter a valid phone number"),
  preferred_date: z.string().min(1, "Please select a date"),
  preferred_time: z.string().min(1, "Please select a time"),
  num_children: z.number().min(1, "Please enter number of children"),
  children_ages: z.string().min(1, "Please enter children's ages"),
  special_needs: z.string().optional(),
});

export default function BookVisitPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [date, setDate] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      parent_name: "",
      email: "",
      phone: "",
      preferred_date: "",
      preferred_time: "",
      num_children: 1,
      children_ages: "",
      special_needs: "",
    },
  });

  const timeSlots = [
    "9:00 AM",
    "10:00 AM",
    "11:00 AM",
    "1:00 PM",
    "2:00 PM",
    "3:00 PM",
    "4:00 PM",
  ];

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);
    if (selectedDate) {
      setValue("preferred_date", format(selectedDate, "yyyy-MM-dd"));
    }
  };

  const WEB3FORMS_KEY = "f954b212-7a47-4712-bda2-99c35d06cd0d";

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      // First, save booking to database
      await axios.post(`${API}/bookings`, data);
      
      // Then send email notification via Web3Forms (FREE)
      try {
        await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            access_key: WEB3FORMS_KEY,
            subject: `🌟 New Visit Booking from ${data.parent_name}`,
            from_name: "Good Hands Family Daycare Website",
            parent_name: data.parent_name,
            email: data.email,
            phone: data.phone,
            preferred_date: data.preferred_date,
            preferred_time: data.preferred_time,
            num_children: data.num_children,
            children_ages: data.children_ages,
            special_needs: data.special_needs || "None",
            message: `
NEW VISIT BOOKING REQUEST

Parent/Guardian: ${data.parent_name}
Email: ${data.email}
Phone: ${data.phone}

Preferred Date: ${data.preferred_date}
Preferred Time: ${data.preferred_time}

Number of Children: ${data.num_children}
Children's Ages: ${data.children_ages}

Special Needs/Questions: ${data.special_needs || "None"}

---
Please contact the parent to confirm the visit.
Good Hands Family Daycare
2689 Travers Place, Warrenton, VA 20187
(571) 535-9999
            `
          }),
        });
      } catch (emailError) {
        console.error("Email notification failed:", emailError);
        // Don't fail the booking if email fails
      }
      
      setIsSubmitted(true);
      toast.success("Visit scheduled successfully!");
    } catch (error) {
      console.error("Error submitting booking:", error);
      toast.error("Failed to schedule visit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="noise-bg min-h-screen pt-32 pb-20" data-testid="booking-success">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border border-orange-100 shadow-xl shadow-orange-500/5 rounded-3xl animate-scale-in">
            <CardContent className="p-12 text-center">
              <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce-slow">
                <CheckCircle className="w-12 h-12 text-emerald-600" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4 animate-fade-in">
                Visit Scheduled!
              </h2>
              <p className="text-slate-600 mb-8 text-lg animate-fade-in-delay-1">
                Thank you for your interest in Good Hands Family Daycare. We've received your visit request and will contact you shortly to confirm.
              </p>
              <div className="bg-yellow-50 rounded-2xl p-6 text-left mb-8 animate-fade-in-delay-2">
                <h3 className="font-semibold text-slate-900 mb-4">What's Next?</h3>
                <ul className="space-y-3 text-slate-700">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold animate-bounce-slow">1</span>
                    <span>We'll call or email you within 24 hours to confirm your visit</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold animate-bounce-slow" style={{animationDelay: '0.2s'}}>2</span>
                    <span>During your visit, you'll meet our team and see our facility</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold animate-bounce-slow" style={{animationDelay: '0.4s'}}>3</span>
                    <span>We'll discuss your child's needs and answer any questions</span>
                  </li>
                </ul>
              </div>
              <Button
                onClick={() => setIsSubmitted(false)}
                className="btn-playful animate-fade-in-delay-3"
                data-testid="schedule-another-btn"
              >
                Schedule Another Visit
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="noise-bg min-h-screen" data-testid="book-visit-page">
      {/* Hero */}
      <section className="pt-32 pb-12 lg:pt-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto animate-fade-in">
            <span className="inline-block bg-yellow-100 text-yellow-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              Schedule a Tour
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              Come Visit Us!
            </h1>
            <p className="text-lg text-slate-600">
              We'd love to show you around and introduce you to our caring team. Fill out the form below to schedule your visit.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              <Card className="border border-orange-100 shadow-xl shadow-orange-500/5 rounded-3xl">
                <CardHeader className="p-8 pb-4">
                  <CardTitle className="text-2xl font-bold text-slate-900">
                    Booking Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-8 pt-4">
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* Parent Info */}
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="parent_name" className="text-slate-700 font-medium">
                          Parent/Guardian Name *
                        </Label>
                        <Input
                          id="parent_name"
                          {...register("parent_name")}
                          placeholder="Your full name"
                          className="rounded-xl border-orange-200 focus:border-yellow-400 focus:ring-yellow-400/30 h-12"
                          data-testid="input-parent-name"
                        />
                        {errors.parent_name && (
                          <p className="text-red-500 text-sm">{errors.parent_name.message}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-700 font-medium">
                          Email Address *
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          {...register("email")}
                          placeholder="your@email.com"
                          className="rounded-xl border-orange-200 focus:border-yellow-400 focus:ring-yellow-400/30 h-12"
                          data-testid="input-email"
                        />
                        {errors.email && (
                          <p className="text-red-500 text-sm">{errors.email.message}</p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-slate-700 font-medium">
                        Phone Number *
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        {...register("phone")}
                        placeholder="(540) 555-0123"
                        className="rounded-xl border-orange-200 focus:border-yellow-400 focus:ring-yellow-400/30 h-12"
                        data-testid="input-phone"
                      />
                      {errors.phone && (
                        <p className="text-red-500 text-sm">{errors.phone.message}</p>
                      )}
                    </div>

                    {/* Date & Time */}
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-slate-700 font-medium">
                          Preferred Date *
                        </Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal h-12 rounded-xl border-orange-200",
                                !date && "text-slate-500"
                              )}
                              data-testid="date-picker-trigger"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {date ? format(date, "PPP") : "Pick a date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={date}
                              onSelect={handleDateSelect}
                              disabled={(date) =>
                                date < new Date() || date.getDay() === 0 || date.getDay() === 6
                              }
                              initialFocus
                              data-testid="calendar"
                            />
                          </PopoverContent>
                        </Popover>
                        {errors.preferred_date && (
                          <p className="text-red-500 text-sm">{errors.preferred_date.message}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label className="text-slate-700 font-medium">
                          Preferred Time *
                        </Label>
                        <Select onValueChange={(value) => setValue("preferred_time", value)}>
                          <SelectTrigger 
                            className="h-12 rounded-xl border-orange-200"
                            data-testid="time-select-trigger"
                          >
                            <SelectValue placeholder="Select a time" />
                          </SelectTrigger>
                          <SelectContent>
                            {timeSlots.map((time) => (
                              <SelectItem key={time} value={time} data-testid={`time-option-${time}`}>
                                {time}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.preferred_time && (
                          <p className="text-red-500 text-sm">{errors.preferred_time.message}</p>
                        )}
                      </div>
                    </div>

                    {/* Children Info */}
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="num_children" className="text-slate-700 font-medium">
                          Number of Children *
                        </Label>
                        <Select 
                          onValueChange={(value) => setValue("num_children", parseInt(value))}
                          defaultValue="1"
                        >
                          <SelectTrigger 
                            className="h-12 rounded-xl border-orange-200"
                            data-testid="num-children-select"
                          >
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 4, 5].map((num) => (
                              <SelectItem key={num} value={num.toString()}>
                                {num} {num === 1 ? "child" : "children"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.num_children && (
                          <p className="text-red-500 text-sm">{errors.num_children.message}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="children_ages" className="text-slate-700 font-medium">
                          Children's Ages *
                        </Label>
                        <Input
                          id="children_ages"
                          {...register("children_ages")}
                          placeholder="e.g., 2 years, 4 years"
                          className="rounded-xl border-orange-200 focus:border-yellow-400 focus:ring-yellow-400/30 h-12"
                          data-testid="input-children-ages"
                        />
                        {errors.children_ages && (
                          <p className="text-red-500 text-sm">{errors.children_ages.message}</p>
                        )}
                      </div>
                    </div>

                    {/* Special Needs */}
                    <div className="space-y-2">
                      <Label htmlFor="special_needs" className="text-slate-700 font-medium">
                        Special Needs or Questions (Optional)
                      </Label>
                      <Textarea
                        id="special_needs"
                        {...register("special_needs")}
                        placeholder="Let us know if your child has any special needs, allergies, or if you have any questions..."
                        className="rounded-xl border-orange-200 focus:border-yellow-400 focus:ring-yellow-400/30 min-h-[120px]"
                        data-testid="input-special-needs"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-playful w-full sm:w-auto"
                      data-testid="submit-booking-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner mr-2" /> Scheduling...
                        </>
                      ) : (
                        "Schedule My Visit"
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Info Sidebar */}
            <div className="lg:col-span-1">
              <Card className="bg-yellow-50 border border-yellow-200 rounded-3xl sticky top-32">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-slate-900 mb-6">
                    Contact Information
                  </h3>
                  <ul className="space-y-6">
                    <li className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-yellow-400 rounded-xl flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-slate-900" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Address</p>
                        <p className="text-slate-600 text-sm">
                          2689 Travers Place<br />
                          Warrenton, VA 20187
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-yellow-400 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-slate-900" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Phone</p>
                        <a 
                          href="tel:+15715359999" 
                          className="text-slate-600 hover:text-yellow-600 text-sm"
                          data-testid="sidebar-phone"
                        >
                          (571) 535-9999
                        </a>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-yellow-400 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-slate-900" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Email</p>
                        <a 
                          href="mailto:Sophiechiepodeu@gmail.com" 
                          className="text-slate-600 hover:text-yellow-600 text-sm"
                          data-testid="sidebar-email"
                        >
                          Sophiechiepodeu@gmail.com
                        </a>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-yellow-400 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-slate-900" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Hours</p>
                        <p className="text-slate-600 text-sm">
                          Monday - Friday<br />
                          7:00 AM - 7:00 PM
                        </p>
                      </div>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
